import { createFileRoute, redirect } from "@tanstack/react-router";
import { Card } from "antd";
import queryClient from "api/queryClient.ts";
import tenantAxiosInstance from "api/tenantAxiosInstance";
import { cascadeProcessApi } from "api/TenantSlug/CascadeProcess/api.ts";
import { Cascade } from "components/UI/Cascade/Cascade.tsx";
import i18next from "i18next";
import { useEffect } from "react";
import { isAxiosErrorOfStatus } from "utils/isAxiosErrorOfStatus.ts";

import GetCascadeProcessExternalFinalPageSettingResponse = App.Http.Responses.Cascade.GetCascadeProcessExternalFinalPageSettingResponse;
import GetConversionApiExternalFinalPageResponse = App.Http.Responses.Conversion.GetConversionApiExternalFinalPageResponse;

const EXTERNAL_FINAL_PAGE_STATUS_CODE = 202;

export const Route = createFileRoute("/$tenantSlug/processes/_layout/$processId/")({
    loader: async ({ params }) => {
        const { tenantSlug, processId } = params;
        tenantAxiosInstance.setTenantSlugInUrl(tenantSlug);

        const [cascadeProcess, setting] = await Promise.all([
            queryClient.fetchQuery({
                queryKey: ["getCascadeProcess", tenantSlug, processId],
                queryFn: () => cascadeProcessApi.show(processId),
            }),
            queryClient.fetchQuery({
                queryKey: ["getCascadeProcessExternalFinalPageSetting", tenantSlug, processId],
                queryFn: () => fetchExternalFinalPageSetting(processId),
            }),
        ]);

        if (cascadeProcess.status !== EXTERNAL_FINAL_PAGE_STATUS_CODE) {
            return;
        }

        const cascade = cascadeProcess.data as GetConversionApiExternalFinalPageResponse;

        if (setting?.skip_internal_loader && cascade.url) {
            throw redirect({ href: cascade.url });
        }
    },
    component: ProcessPage,
});

async function fetchExternalFinalPageSetting(
    processId: string
): Promise<GetCascadeProcessExternalFinalPageSettingResponse | undefined> {
    try {
        return await cascadeProcessApi.showExternalFinalPageSetting(processId);
    } catch (error) {
        if (isAxiosErrorOfStatus(error, 404)) {
            return undefined;
        }

        throw error;
    }
}

function ProcessPage() {
    const params = Route.useParams();

    //temp guard to make sure
    useEffect(() => {
        i18next.changeLanguage("cs");
    }, []);

    return (
        <Card className="rounded-md">
            <Cascade processId={params.processId} />
        </Card>
    );
}
